/**
 * Purchase Order Status Management Hooks with Optimistic Updates
 *
 * Provides optimistic updates for purchase order status changes with automatic rollback on failure.
 */

import { useQuery } from '@tanstack/react-query';

import { useAuth } from '../../../hooks/useAuth.js';
import { useOptimisticStatusUpdate } from '../../../hooks/useOptimisticUpdate.js';
import { purchaseOrderStatusExtensions } from '../../extensions/purchase-order-status-business-logic-extension.js';
import { purchaseOrdersExtensions } from '../../extensions/purchase_orders-business-logic-extension.js';
import { purchaseOrdersKeys } from '../../generated_hooks/generated-purchase_orders-hooks.js';
import type { PurchaseOrderStatuses } from '../../generated_schemas/generated-purchase_order_statuses-schema.js';
import type { PurchaseOrders, PurchaseOrdersUpdate } from '../../generated_schemas/generated-purchase_orders-schema.js';

/**
 * Query keys factory for purchase order status management
 */
export const purchaseOrderStatusManagementKeys = {
  all: ['purchase-order-status-management'] as const,
  byOrganization: (orgId: string) => [...purchaseOrderStatusManagementKeys.all, 'organization', orgId] as const,
  default: (orgId: string) => [...purchaseOrderStatusManagementKeys.all, 'default', orgId] as const,
};

export interface OptimisticUpdatePurchaseOrderStatusParams {
  purchaseOrderId: string;
  statusId: string;
  statusName: string;
  statusColor?: string | null;
}

export interface PurchaseOrderStatusManagementOptimisticResult {
  // Available statuses query
  availableStatuses: PurchaseOrderStatuses[] | undefined;
  isLoadingStatuses: boolean;
  statusesError: Error | null;

  // Optimistic update
  updatePurchaseOrderStatus: (params: OptimisticUpdatePurchaseOrderStatusParams, currentPO: PurchaseOrders) => Promise<PurchaseOrders>;
  isUpdatingStatus: boolean;
  updateError: Error | null;
  isRollingBack: boolean;

  // Utility methods
  refetchStatuses: () => void;
  resetUpdateError: () => void;
}

/**
 * Hook for managing purchase order status operations with optimistic updates
 *
 * Features:
 * - Optimistic UI updates (immediate visual feedback)
 * - Automatic rollback if server update fails
 * - Proper error handling with user notifications
 * - Cache invalidation for data consistency
 * - Loading states that don't block user interaction
 *
 * @param organizationId - Organization ID (defaults to test org for now)
 * @returns Object with queries, mutations, and loading states
 */
export const usePurchaseOrderStatusManagementOptimistic = (): PurchaseOrderStatusManagementOptimisticResult => {
  const { organizationId } = useAuth();

  // Query for available purchase order statuses
  const {
    data: statusesResponse,
    isLoading: isLoadingStatuses,
    error: statusesError,
    refetch: refetchStatuses
  } = useQuery({
    queryKey: purchaseOrderStatusManagementKeys.byOrganization(organizationId || ''),
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return purchaseOrderStatusExtensions.getPurchaseOrderStatusesByOrganization(organizationId);
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - statuses don't change often
    gcTime: 10 * 60 * 1000, // 10 minutes
    enabled: !!organizationId,
  });

  // Optimistic update hook
  //
  // Cache invalidation is intentionally aggressive here. The PO detail
  // panel (PurchaseOrderDetails) reads via usePurchaseOrders(id) which
  // is keyed at purchaseOrdersKeys.detail(id). React Query's
  // invalidateQueries with a parent key (purchaseOrdersKeys.all) should
  // prefix-match and refresh the detail query — but browser testing
  // surfaced a "stale side-drawer header" symptom where the row badge
  // updated but the open detail panel kept the prior status until
  // manual reload. The most likely culprit is timing between the
  // optimistic update's setQueryData on the LIST keys and the detail
  // query's stale-while-revalidate flow.
  //
  // Defensive fix: keep the prefix invalidation (covers any
  // unenumerated reader) AND drop a specific entry for the changed
  // PO's detail key with the actively-observed-only refetch hint
  // baked in by useOptimisticStatusUpdate's flat-invalidate loop. The
  // detail key needs to be computed at update time because it depends
  // on the PO id — we slot it into the static list using a sentinel
  // that the update wrapper expands.
  const optimisticUpdate = useOptimisticStatusUpdate<PurchaseOrders>({
    entityType: 'Purchase Order',
    updateFn: async (purchaseOrderId: string, statusId: string) => {
      const updateData: PurchaseOrdersUpdate = {
        status_id: statusId,
        updated_at: new Date().toISOString()
      };

      return purchaseOrdersExtensions.update(purchaseOrderId, updateData);
    },
    getCurrentStatus: (po: PurchaseOrders) => ({
      id: po.status_id || '',
      name: 'Unknown' // POs don't have trigger fields for status name/color
    }),
    createUpdatedEntity: (po: PurchaseOrders, statusId: string, _statusName: string, _statusColor?: string | null) => ({
      ...po,
      status_id: statusId,
      updated_at: new Date().toISOString()
    }),
    invalidateKeys: [
      purchaseOrdersKeys.all,
      purchaseOrdersKeys.lists(),       // explicit list-shape invalidation
      purchaseOrdersKeys.details(),     // explicit detail-shape invalidation (matches every usePurchaseOrders(id))
      ['purchase-orders'],              // Legacy compatibility (dash-cased key)
      purchaseOrderStatusManagementKeys.all,
      ['tracker-purchase-orders-v2'],   // PO tracker query — refreshes the activity summary
      ['purchase-order-changelogs'],    // History tab — DB triggers wrote new changelog rows; refetch so the timeline shows them live
    ]
  });

  // Wrapper function that matches the interface
  const updatePurchaseOrderStatus = async (
    params: OptimisticUpdatePurchaseOrderStatusParams,
    currentPO: PurchaseOrders
  ): Promise<PurchaseOrders> => {
    return optimisticUpdate.execute(currentPO, {
      entityId: params.purchaseOrderId,
      statusId: params.statusId,
      statusName: params.statusName,
      statusColor: params.statusColor
    });
  };

  return {
    // Available statuses
    availableStatuses: statusesResponse?.data,
    isLoadingStatuses,
    statusesError: statusesError as Error | null,

    // Optimistic update
    updatePurchaseOrderStatus,
    isUpdatingStatus: optimisticUpdate.isPending,
    updateError: optimisticUpdate.error,
    isRollingBack: optimisticUpdate.isRollingBack,

    // Utility methods
    refetchStatuses: () => refetchStatuses(),
    resetUpdateError: optimisticUpdate.resetError,
  };
};

/**
 * Hook for getting default purchase order status for an organization
 *
 * @param organizationId - Organization ID (defaults to test org for now)
 * @returns React Query result with default status
 */
export const useDefaultPurchaseOrderStatusOptimistic = () => {
  const { organizationId } = useAuth();

  return useQuery({
    queryKey: purchaseOrderStatusManagementKeys.default(organizationId || ''),
    queryFn: () => {
      if (!organizationId) throw new Error('Organization ID required');
      return purchaseOrderStatusExtensions.getDefaultPurchaseOrderStatus(organizationId);
    },
    staleTime: 10 * 60 * 1000, // 10 minutes - default status rarely changes
    gcTime: 15 * 60 * 1000, // 15 minutes
    enabled: !!organizationId,
  });
};
